import React, { useState, useEffect, useContext, useRef } from "react";
import { NativeSelect, Text, Stack, Box } from "@mantine/core";
import FloatingLabelInput from "./floating-label-input";
import { DataContext } from "../context/DataContext";
// `dayFirst` switches the single-input mask from MM/DD/YYYY to DD/MM/YYYY
// (UK, DE). The wire format handed to onDobChange is unchanged: the parent
// always receives { day, month, year } and builds YYYYMMDD from it.
const DateOfBirthSelect = ({
  onDobChange,
  isSinglePageForm = false,
  dayFirst = false,
  placeholder,
  ...props
}) => {
  const { text } = useContext(DataContext);

  // Seed local state synchronously from props.value so remounts (e.g.
  // multi-step navigation back to the DOB step) restore the already-
  // entered value without a flicker through the empty state. The
  // empty-state would otherwise let the dob → onDobChange effect race
  // and clear the parent form value before Effect 1 could repopulate
  // local state.
  const parseRaw = (raw) => {
    const v = (raw ?? "").replace(/[^0-9]/g, "");
    if (v.length === 8) {
      return {
        year: v.substring(0, 4),
        month: v.substring(4, 6),
        day: v.substring(6, 8),
      };
    }
    return { day: "", month: "", year: "" };
  };

  const [dob, setDob] = useState(() => parseRaw(props.value));
  const [singleInputValue, setSingleInputValue] = useState(() => {
    const seeded = parseRaw(props.value);
    if (!seeded.year) return "";
    return dayFirst
      ? `${seeded.day}/${seeded.month}/${seeded.year}`
      : `${seeded.month}/${seeded.day}/${seeded.year}`;
  });
  const lastSubmittedDobRef = useRef(
    (props.value ?? "").replace(/[^0-9]/g, "").length === 8 ? props.value : "",
  );

  const dobLabel =
    text?.general?.dateOfBirth ||
    text?.general?.dob ||
    text?.general?.dateOfBirthLabel ||
    "Date of birth";

  useEffect(() => {
    const rawValue = props.value ?? "";

    // When the form hasn't provided a new value, avoid resetting the local
    // state so users can continue typing without the input clearing itself.
    if (rawValue === lastSubmittedDobRef.current) {
      return;
    }

    if (rawValue.length === 8) {
      const year = rawValue.substring(0, 4);
      const month = rawValue.substring(4, 6);
      const day = rawValue.substring(6, 8);
      setDob({ day, month, year });
      lastSubmittedDobRef.current = `${year}${month}${day}`;

      if (isSinglePageForm) {
        setSingleInputValue(
          dayFirst ? `${day}/${month}/${year}` : `${month}/${day}/${year}`,
        );
      }
    } else if (rawValue === "") {
      setDob({ day: "", month: "", year: "" });
      lastSubmittedDobRef.current = "";
      if (isSinglePageForm) {
        setSingleInputValue("");
      }
    }
  }, [props.value, isSinglePageForm, dayFirst]);

  useEffect(() => {
    if (typeof onDobChange !== "function") {
      return;
    }

    // Trigger onDobChange with full DOB when complete; only clear when fully empty
    const isComplete =
      dob.day &&
      dob.month &&
      dob.year &&
      dob.day.length === 2 &&
      dob.month.length === 2 &&
      dob.year.length === 4;

    if (isComplete) {
      const formattedDob = `${dob.year}${dob.month}${dob.day}`;

      if (lastSubmittedDobRef.current !== formattedDob) {
        lastSubmittedDobRef.current = formattedDob;
        onDobChange(dob);
      }
    } else if (!dob.day && !dob.month && !dob.year) {
      if (lastSubmittedDobRef.current !== "") {
        lastSubmittedDobRef.current = "";
        onDobChange({ day: "", month: "", year: "" });
      }
    }
  }, [dob, onDobChange]);

  const handleSingleInputChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 8);

    let formattedValue = digitsOnly;
    if (digitsOnly.length > 4) {
      formattedValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
    } else if (digitsOnly.length > 2) {
      formattedValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }

    setSingleInputValue(formattedValue);

    // First two digits are the day in day-first regions, the month otherwise.
    const first = digitsOnly.slice(0, 2);
    const second = digitsOnly.length >= 4 ? digitsOnly.slice(2, 4) : "";
    setDob({
      month: dayFirst ? second : first,
      day: dayFirst ? first : second,
      year: digitsOnly.length === 8 ? digitsOnly.slice(4, 8) : "",
    });
  };

  const months = [
    { value: "", label: "" },
    { value: "01", label: text.general.months.january },
    { value: "02", label: text.general.months.february },
    { value: "03", label: text.general.months.march },
    { value: "04", label: text.general.months.april },
    { value: "05", label: text.general.months.may },
    { value: "06", label: text.general.months.june },
    { value: "07", label: text.general.months.july },
    { value: "08", label: text.general.months.august },
    { value: "09", label: text.general.months.september },
    { value: "10", label: text.general.months.october },
    { value: "11", label: text.general.months.november },
    { value: "12", label: text.general.months.december },
  ];

  const handleInputChange = (field, value) => {
    if (field === "month") {
      setDob((prevState) => ({
        ...prevState,
        month: value,
      }));
      return;
    }

    const maxLength = field === "year" ? 4 : 2;
    const sanitizedValue = value.replace(/\D/g, "").slice(0, maxLength);

    setDob((prevState) => ({
      ...prevState,
      [field]: sanitizedValue,
    }));
  };

  return (
    <Box style={{ width: "100%" }}>
      <Stack spacing="xs" style={{ width: "100%" }}>
        {!isSinglePageForm && (
          <Text size="sm" weight={600} color="dimmed" style={{ marginBottom: "0px" }}>
            {dobLabel}
          </Text>
        )}
        {props.error && !isSinglePageForm && (
          <Text fz="xs" c="red">
            {props.error}
          </Text>
        )}
        {isSinglePageForm ? (
          <FloatingLabelInput
            size="xl"
            placeholder={placeholder || (dayFirst ? "DD/MM/YYYY" : "MM/DD/YYYY")}
            label={dobLabel}
            value={singleInputValue}
            customOnChange={(event) => handleSingleInputChange(event.target.value)}
            error={props.error}
            inputMode="numeric"
            type="tel"
            disabled={props.disabled}
          />
        ) : (
          <Stack spacing="sm">
            <NativeSelect
              size="xl"
              data={months}
              placeholder="Pick one"
              onChange={(e) => handleInputChange("month", e.currentTarget.value)}
              label={text.general.month}
              value={dob.month || ""}
              disabled={props.disabled}
              styles={(theme) => ({
                input: {
                  fontSize: "20px",
                  paddingTop: "18px",
                  lineHeight: "1.2",
                },
                label: {
                  position: "absolute",
                  pointerEvents: "none",
                  fontSize: "11px",
                  paddingInlineStart: "10px",
                  paddingTop: "5px",
                  zIndex: "1",
                  color: "#868e96",
                },
                root: {
                  position: "relative",
                },
              })}
            />
            <FloatingLabelInput
              size="xl"
              placeholder="DD"
              label={text.general.day}
              value={dob.day}
              onChange={(e) => handleInputChange("day", e.currentTarget.value)}
              maxLength={2}
              minLength={2}
              type="tel"
              inputMode="numeric"
              pattern="\d*"
              disabled={props.disabled}
            />
            <FloatingLabelInput
              size="xl"
              placeholder="YYYY"
              label={text.general.year}
              value={dob.year}
              onChange={(e) => handleInputChange("year", e.currentTarget.value)}
              maxLength={4}
              minLength={4}
              type="tel"
              inputMode="numeric"
              pattern="\d*"
              disabled={props.disabled}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default DateOfBirthSelect;
